import { useCallback, useEffect, useState } from 'react';

import { staticRoutes } from 'Deer/constants/routes';
import { staticRoutesWithCustomSidebar } from 'Deer/constants/staticRoutesWithCustomSidebar';
import { useOrganizationSettings } from 'Deer/intent/Settings/Platform/hooks/dataHooks';
import { verifyNetchexNotificationApi } from 'api/employee';
import queryString from 'query-string';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { NETCHEX_MESSAGES_TYPES } from 'screens/NonAuthenticatedNetchexMessage/NonAuthenticatedNetchexMessage';
import { useGoalConfigurations, useGoalFrequencyData } from 'screens/admin/Objectives/GoalPreferences/hooks/dataHooks';
import getBodyForAuthenticationCall from 'utils/helpers/getBodyForAuthenticationCall';

import { isNetchexLogin } from 'constants/netChexConstants';
import { routes } from 'constants/routes';

import { authenticate, authenticateNetchex } from 'services/employee';

import { useLoggedInUsersAccessesQuery } from '../useLoggedInUsersAccessesQuery';
import { checkIsExpiredJWTToken, refreshNetchexLogin } from './netchexAuthHelper';

const checkIsReviewsChartPage = (location) => {
  const { params } =
    matchPath(location.pathname, {
      path: [staticRoutesWithCustomSidebar.reviewsResultChartOnlyPage],
      exact: true,
    }) ?? {};
  return !!params;
};

const authenticateUser = async ({ setAuthenticated, location, history }) => {
  const domain = window.location.hostname;

  // these query params are applicable in case of netchex notification link
  const { internalEmpId, 'deeplink.role': netchexRole } = queryString.parse(location.search);

  // localhost domain check is so that we can check rest of the netchex rendering and styles etc in localhost without the netchex authentication(cause it will fail in local)
  // mac.local is a custom Local hostname for mac, that is used for iphone debugging of webpage
  const isNetchexLoginBool = isNetchexLogin && domain !== 'localhost' && domain !== 'mac.local';

  const isReviewsChartPage = checkIsReviewsChartPage(location);

  try {
    const body = getBodyForAuthenticationCall({
      path: location.pathname,
      locationSearch: location.search,
    });

    let res;
    // reviews chart page is opened in puppeteer in lambda so we can't authenticate it the netchex way. Also that isn't required anyway,
    // as we also pass the authToken in the lambda call, we can just use that to rely on our usual cookie authentication
    if (isNetchexLoginBool && !isReviewsChartPage) {
      try {
        const jwtToken = window.localStorage.getItem('Authorization');
        if (!jwtToken) {
          // this will do the redirection to netchex login page in catch block
          console.error('No JWT token found in localStorage');
          throw new Error('No JWT token found in localStorage');
        }
        const cleanJwtToken = jwtToken?.slice(7); // remove 'Bearer ' from front
        const isTokenExpired = checkIsExpiredJWTToken(cleanJwtToken);
        if (isTokenExpired) {
          refreshNetchexLogin();
          return;
        }
        res = await authenticateNetchex({ jwtToken: cleanJwtToken });
      } catch (err) {
        // case when the netchex user is there but no corresponding user on mesh
        if (err.status === 500 && (err.message ?? '').startsWith('Mesh user not found for')) {
          const splitMessage = err.message.split(' ');
          const email = splitMessage[splitMessage.length - 1];
          const urlParams = new URLSearchParams();
          urlParams.set('messageType', NETCHEX_MESSAGES_TYPES.NO_MESH_USER);
          // redirectUrl is just to forward to message page which is then used in login page link
          urlParams.set('redirectUrl', window.location.href);
          urlParams.set('email', email);
          history.push(`${routes.nonAuthNetchexMessage}?${urlParams.toString()}`);
          return null;
        }
        const urlParams = new URLSearchParams();
        urlParams.set('redirectUrl', window.location.href);
        window.location.href = `${process.env.PUBLIC_APP_NETCHEX_LOGIN_URL}?${urlParams.toString()}`;
        return null;
      }

      if (internalEmpId && netchexRole) {
        try {
          await verifyNetchexNotificationApi({ internalEmpId, netchexRole });
        } catch (err) {
          if (err.status === 401 && err.message) {
            const urlParams = new URLSearchParams();
            urlParams.set('messageType', NETCHEX_MESSAGES_TYPES.API_MESSAGE_TEXT);
            urlParams.set('apiMessageText', err.message);
            urlParams.set('redirectUrl', window.location.href);

            history.push(`${routes.nonAuthNetchexMessage}?${urlParams.toString()}`);
            return null;
          }

          const urlParams = new URLSearchParams();
          urlParams.set('redirectUrl', window.location.href);
          window.location.href = `${process.env.PUBLIC_APP_NETCHEX_LOGIN_URL}?${urlParams.toString()}`;
          return null;
        }
      }
    } else {
      res = await authenticate(body);
    }

    const orgId = res?.employee?.organization?.id;

    return {
      res,
      orgId,
    };
  } catch (err) {
    if (err.status === 403 || err.status === 500 || err.status === 400) {
      window.location.href = routes.home;
    } else if (err.status === 401) {
      setAuthenticated(false);
      history.push(staticRoutes.login);
    } else {
      setAuthenticated(false);
      history.push(staticRoutes.login);
    }
  }
};

export const useAuthentication = ({
  setAuthenticated,
  setLoggedInUser,
  setOrgData,
  setEnabledFeatureFlags,
  setEmployeeGoalsAccessPrivileges,
  addSentryScope,
  goToSignUpPageOrHomePage,
  isNonAuthRoute = false,
}) => {
  const history = useHistory();
  const location = useLocation();

  const [empId, setEmpId] = useState<string | null>(null);
  const [organizationId, setOrganizationId] = useState<number | null>(null);
  const [employeeData, setEmployeeData] = useState(null);

  const { isLoading: isLoadingEmployeeAccesses } = useLoggedInUsersAccessesQuery(empId);

  const { isLoading: isLoadingOrganisation } = useOrganizationSettings(organizationId);

  const { isLoading: isLoadingObjectiveSettings } = useGoalFrequencyData(!!organizationId);

  useGoalConfigurations(!!empId);

  const handleAuthSuccess = useCallback(
    (res) => {
      if (!isLoadingEmployeeAccesses && !isLoadingOrganisation && !!res) {
        // const orgDataFromAuthApi = {
        //   defaultGoalDueTS: res.defaultGoalDueTS,
        //   defaultGoalStartTS: res.defaultGoalStartTS,
        //   okrConfigData: res.okrConfigData,
        //   includeOwnerGoals: res.includeOwnerGoals,
        //   includeCollaboratorGoals: res.includeCollaboratorGoals,
        //   goalCustomNamesConfig: res.goalCustomNamesConfig,
        // };
        setLoggedInUser(res.employee);
        // setOrgData(orgDataFromAuthApi);
        addSentryScope(res.employee);
        setEnabledFeatureFlags(res.enabledFeatureFlags);
        setAuthenticated(true);

        // setEmployeeGoalsAccessPrivileges(res?.accessPrivileges);

        goToSignUpPageOrHomePage({
          res,
          push: history.push,
          path: location.pathname,
          queryString: location.search,
          replaceState: history.replace,
        });
      }
    },
    [
      addSentryScope,
      goToSignUpPageOrHomePage,
      history.push,
      history.replace,
      isLoadingEmployeeAccesses,
      isLoadingObjectiveSettings,
      isLoadingOrganisation,
      location.pathname,
      location.search,
      setAuthenticated,
      setEmployeeGoalsAccessPrivileges,
      setEnabledFeatureFlags,
      setLoggedInUser,
      setOrgData,
    ]
  );

  useEffect(() => {
    if (isNonAuthRoute) {
      return;
    }
    authenticateUser({
      setAuthenticated,
      location,
      history,
    }).then((data) => {
      const { orgId, res } = data ?? {};
      if (res) {
        setEmpId(res.employee.id);
        setOrganizationId(orgId);
        setEmployeeData(res);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.push, location.pathname, location.search]);

  useEffect(() => {
    if (empId) {
      handleAuthSuccess(employeeData);
    }
  }, [
    isLoadingEmployeeAccesses,
    isLoadingOrganisation,
    isLoadingObjectiveSettings,
    empId,
    handleAuthSuccess,
    employeeData,
  ]);
};
